import type { IBaseResponse } from '@/types/base-response';
import type { QueryFunctionContext } from '@tanstack/react-query';
import qs from 'qs';
import type {
  CompanyConfig,
  CompanyDetail,
  ContactDetail,
  GoogleCalendar,
  GroupType,
  PromoBanner,
  Reseller,
  Tax,
} from './company.types';
import bookingInstance from '@/lib/axios/booking-instance';
import type { InferType } from 'yup';
import type {
  companyConfigValidationSchema,
  companyDetailValidationSchema,
  contactDetailValidationSchema,
  googleCalendarShareValidationSchema,
  groupTypeSettingValidationSchema,
  promoBannerValidationSchema,
  resellerSettingValidationSchema,
  taxSettingValidationSchema,
} from './company.validations';
import axios, { AxiosError } from 'axios';
import { handleRequest } from '@/lib/api/factories/base-api-factory';

type CompanyDetailPayload = InferType<typeof companyDetailValidationSchema>;

// Tax Setting
const getListTax = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<Tax.ITax[]>> => {
  const response = await bookingInstance.get(`/tax/`, {
    params: queryKey[1],
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data;
};

const getTaxById = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<Tax.ITaxDetail>> => {
  const response = await bookingInstance.get(`/tax/${queryKey[1]}/`);

  return response.data;
};

const createTax = async (
  data: InferType<typeof taxSettingValidationSchema>
) => {
  try {
    const response = await bookingInstance.post('/tax', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const editTax = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof taxSettingValidationSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/tax/${id}`, data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

// Contact Detail
const getListContactDetail = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  IBaseResponse<ContactDetail.IContactDetail[]>
> => {
  const response = await bookingInstance.get(`/contact-detail/`, {
    params: queryKey[1],
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data;
};

const getContactDetailById = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  IBaseResponse<ContactDetail.IContactDetailDetail>
> => {
  const response = await bookingInstance.get(`/contact-detail/${queryKey[1]}/`);

  return response.data;
};

const createContactDetail = async (
  data: InferType<typeof contactDetailValidationSchema>
) => {
  try {
    const response = await bookingInstance.post('/contact-detail', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const editContactDetail = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof contactDetailValidationSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/contact-detail/${id}`, data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

// Reseller Setting
const getResellerList = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<Reseller.IReseller[]>> => {
  const response = await bookingInstance.get(`/reseller/`, {
    params: queryKey[1],
    /**
     * `repeat`, so a one-element `branchIds` goes out flat as
     * `branchIds=<uuid>`. That is the only form the endpoint applies the
     * location filter to — the bracketed form never binds to the DTO (it skips
     * `@IsUUID` and returns the full unfiltered list). Every other reseller
     * filter is a scalar, so this changes nothing for callers sending no array.
     */
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data;
};

const getResellerById = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<Reseller.IResellerDetail>> => {
  const response = await bookingInstance.get(`/reseller/${queryKey[1]}/`);

  return response.data;
};

const createReseller = async (
  data: InferType<typeof resellerSettingValidationSchema>
) => {
  try {
    const response = await bookingInstance.post('/reseller', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const editReseller = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof resellerSettingValidationSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/reseller/${id}`, data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

// Group Type Setting
const getGroupTypeList = async ({
  queryKey,
}: QueryFunctionContext): Promise<IBaseResponse<GroupType.IGroupType[]>> => {
  const response = await bookingInstance.get(`/group-type/`, {
    params: queryKey[1],
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data;
};

const getGroupTypeById = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  IBaseResponse<GroupType.IGroupTypeDetail>
> => {
  const response = await bookingInstance.get(`/group-type/${queryKey[1]}/`);

  return response.data;
};

const createGroupType = async (
  data: InferType<typeof groupTypeSettingValidationSchema>
) => {
  try {
    const response = await bookingInstance.post('/group-type', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

/**
 * `updateGroupType` only declares name + status, so the payload is narrowed
 * here rather than forwarding the whole form — sending `companyId` back on an
 * edit is not something the endpoint accepts.
 */
const editGroupType = async ({
  id,
  data,
}: {
  id: string;
  data: InferType<typeof groupTypeSettingValidationSchema>;
}) => {
  try {
    const response = await bookingInstance.put(`/group-type/${id}`, {
      name: data.name,
      status: data.status,
    });

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const getBanner = () => {
  return handleRequest<IBaseResponse<PromoBanner.IPromoBanner>>(
    bookingInstance.get('/banner')
  );
};

const createBanner = (data: InferType<typeof promoBannerValidationSchema>) => {
  return handleRequest(bookingInstance.post('/banner', data));
};

const editBanner = (data: InferType<typeof promoBannerValidationSchema>) => {
  return handleRequest(bookingInstance.put('/banner', data));
};

// Google Calendar
const getGoogleCalendarList = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  IBaseResponse<GoogleCalendar.IGoogleCalendar[]> & {
    summary?: GoogleCalendar.IGoogleCalendarSummary;
  }
> => {
  const response = await bookingInstance.get(`/google-calendar/`, {
    params: queryKey[1],
    paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
  });

  return response.data;
};

const shareGoogleCalendar = async (
  data: InferType<ReturnType<typeof googleCalendarShareValidationSchema>>
) => {
  try {
    const response = await bookingInstance.post('/google-calendar/share', data);

    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }

    throw err;
  }
};

const getGoogleCalendarByEmail = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  IBaseResponse<GoogleCalendar.IGoogleCalendar>
> => {
  const email = encodeURIComponent(String(queryKey[1] ?? ''));
  const response = await bookingInstance.get(`/google-calendar/share/${email}`);
  return response.data;
};

// OAuth connection (status & auth url). bookingInstance auto-attach JWT,
// jadi BE bisa baca user.id buat per-user binding & state param.
const getGoogleStatus = async (): Promise<GoogleCalendar.IConnectionStatus> => {
  const response = await bookingInstance.get('/google/status');
  return response.data;
};

const getGoogleAuthUrl = async (): Promise<GoogleCalendar.IAuthUrl> => {
  const response = await bookingInstance.get('/google/auth');
  return response.data;
};

const disconnectGoogle =
  async (): Promise<GoogleCalendar.IDisconnectResult> => {
    const response = await bookingInstance.delete('/google/disconnect');
    return response.data;
  };

// Bind lokasi <-> subcalendar
const getGoogleSubCalendars = async (): Promise<
  IBaseResponse<GoogleCalendar.ISubCalendarOption[]>
> => {
  const response = await bookingInstance.get('/google-calendar/subcalendars');
  return response.data;
};

const getGoogleBindingSummary =
  async (): Promise<GoogleCalendar.IBindingSummary> => {
    const response = await bookingInstance.get('/google-calendar/summary');
    return response.data;
  };

const getGoogleBindings = async (): Promise<
  IBaseResponse<GoogleCalendar.ILocationBinding[]>
> => {
  const response = await bookingInstance.get('/google-calendar/bindings');
  return response.data;
};

const bindLocationCalendar = async (payload: {
  locationId: string;
  calendarId: string;
}): Promise<GoogleCalendar.IBindResult> => {
  try {
    const response = await bookingInstance.post(
      '/google-calendar/bind',
      payload
    );
    return response.data;
  } catch (err) {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }
    throw err;
  }
};

const unbindLocationCalendar = async (
  locationId: string
): Promise<GoogleCalendar.IBindResult> => {
  const response = await bookingInstance.delete(
    `/google-calendar/bind/${locationId}`
  );
  return response.data;
};

// Company Detail (branding for Betaaloverzicht PDF). Fetched by company id.
const getCompanyDetail = async (
  id: string
): Promise<IBaseResponse<CompanyDetail.ICompanyDetail>> => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_APP_BOOKING_API_URL}/company-detail/${encodeURIComponent(id)}`
    );
    return response.data;
  } catch (err) {
    // No branding configured yet — treat 404 as "empty", not an error.
    if (err instanceof AxiosError && err.response?.status === 404) {
      return { meta: { success: true }, data: undefined };
    }
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }
    throw err;
  }
};

// Create needs the owning company id (the logged-in user's company).
type CompanyDetailCreatePayload = CompanyDetailPayload & { companyId: string };

const createCompanyDetail = (data: CompanyDetailCreatePayload) =>
  handleRequest(bookingInstance.post('/company-detail', data));

const editCompanyDetail = ({
  id,
  data,
}: {
  id: string;
  data: CompanyDetailCreatePayload;
}) => handleRequest(bookingInstance.put(`/company-detail/${id}`, data));

// Company Config — per-company secrets, encrypted at rest by the backend.
// The company is resolved from the bearer token, so no id is sent.
// `companyId` is only honoured for the super Admin; a Company Admin is always
// scoped to their own company by the API, whatever is passed here.
const getCompanyConfig = async (
  companyId?: string
): Promise<IBaseResponse<CompanyConfig.ICompanyConfigView>> => {
  try {
    const response = await bookingInstance.get('/company-config', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  } catch (err) {
    // Nothing configured yet — treat 404 as "empty", not an error.
    if (err instanceof AxiosError && err.response?.status === 404) {
      return { meta: { success: true }, data: undefined };
    }
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }
    throw err;
  }
};

// Only the keys present in the payload are written; omitted keys keep their
// stored value.
const upsertCompanyConfig = (
  data: Partial<InferType<typeof companyConfigValidationSchema>> & {
    companyId?: string;
  }
) => handleRequest(bookingInstance.put('/company-config', data));

const removeCompanyConfig = ({
  key,
  companyId,
}: {
  key: CompanyConfig.ConfigKey;
  companyId?: string;
}) =>
  handleRequest(
    bookingInstance.delete(`/company-config/${key}`, {
      params: companyId ? { companyId } : undefined,
    })
  );

export const CompanyService = {
  getCompanyConfig,
  upsertCompanyConfig,
  removeCompanyConfig,
  getCompanyDetail,
  createCompanyDetail,
  editCompanyDetail,
  getListTax,
  getTaxById,
  createTax,
  editTax,
  getListContactDetail,
  getContactDetailById,
  createContactDetail,
  editContactDetail,
  getResellerList,
  getResellerById,
  createReseller,
  editReseller,
  getGroupTypeList,
  getGroupTypeById,
  createGroupType,
  editGroupType,
  getBanner,
  createBanner,
  editBanner,
  getGoogleCalendarList,
  getGoogleCalendarByEmail,
  shareGoogleCalendar,
  getGoogleStatus,
  getGoogleAuthUrl,
  disconnectGoogle,
  getGoogleSubCalendars,
  getGoogleBindingSummary,
  getGoogleBindings,
  bindLocationCalendar,
  unbindLocationCalendar,
};
